import React from 'react';
import { useIntl } from 'react-intl';

import { styled } from '@mui/material/styles';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';

import EnhancedDivider from 'components/EnhancedDivider';

const DividerText = styled('div')(({ theme }) => ({
  position: 'absolute',
  height: '44px',
  top: '2px',
  paddingRight: '8px',
  backgroundColor: '#fff',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'flex-start',
  alignItems: 'center',
}));

const sizeMapping = {
  1: 3,
  2: 6,
  3: 9,
  4: 12,
}

const RootStyle = {
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
};

const Spacer = styled('div')(() => ({
  height: '40px',
  width: '100%',
  display: 'block',
  flexGrow: 1,
}));

function PrintCustomerData({ printFields, customerData, customerExtData, userMapping, customerMapping, customerTypeMapping, agreement }) {
  const { formatMessage } = useIntl()

  const flatData = Object.keys(customerExtData ?? {}).reduce((acc, cur) => {
    if (cur !== 'id') {
      acc = { ...acc, ...customerExtData[cur] }
    }
    return acc
  }, {})

  function createField(field, value, customer) {
    let newValue = customerData[field.name] ? customerData[field.name] : ''
    let labelText = ''

    if (field.name === 'salesRep') {
      newValue = customer.salesRepName
    }



    if (field.type === '-') {
      if (field.text) {
        return <Grid item key={field.name} xs={12} sm={field.sm} md={12} sx={{ position: 'relative' }}>
          <Divider sx={{ mt: '8px', mb: '8px' }} />
          <DividerText>
            <Typography color="textSecondary" variant="caption">{field.text}</Typography>
          </DividerText>
        </Grid>
      } else {
        return <Grid item key={field.name} xs={12} sm={field.sm} md={12}></Grid>
      }
    } else if (field.type === 'status') {
      return <Grid item key={field.name} xs={3} sm={field.sm} md={2} sx={{ position: 'relative' }}>
        <Chip sx={{ backgroundColor: field.color, color: '#fff', fontSize: '15px', minWidth: '100px' }} label={formatMessage({ id: `customer.profile.status.${field.name}` })} />
      </Grid>
    } else if (field.type === 'emergencyContact') {
      labelText = formatMessage({ id: `customer.profile.emergencyContacts.${field.name}` })
      newValue = customerData['ICE'][value][field.name]

      return <Grid item key={field.name} xs={12} sm={field.sm} md={field.md}>
        <TextField
          multiline={field.multiline}
          type="text"
          label={labelText}
          value={newValue}
          fullWidth
          variant="standard"
          size="small"
          margin="dense"
          InputProps={{
            readOnly: true,
          }}
        />
      </Grid>
    } else if (field.type === 'storedValue') {
      return <Grid item key={field.name} xs={12} sm={field.sm} md={field.md}>
        <TextField
          multiline={field.multiline}
          type="text"
          label={field.name}
          value={field.value}
          fullWidth
          variant="standard"
          size="small"
          margin="dense"
          InputProps={{
            readOnly: true,
          }}
        />
      </Grid>
    }

    if (field.name === 'customerId') {
      // const birthDateM = customerData.birthDate ? customerData.birthDate.split('-')[1] : ''
      // const birthDateD = customerData.birthDate ? customerData.birthDate.split('-')[2] : ''
      newValue = customerData.code
    } else if (field.name === 'type') {
      newValue = customerTypeMapping[value] ? customerTypeMapping[value].name : ''
    } else if (['gender', 'maritalStatus', 'nationality', 'infosource'].includes(field.name) && value) {
      if (field.name === 'infosource' &&
          !['advertising',
            'newspapermagazine',
            'officialwebsite',
            'facebook',
            'blog',
            'google',
            'propagandamaterial',
            'line',
            'doctor',
            'instagram'].includes(value)) {
        newValue = `${formatMessage({ id: `customer.profile.${field.name}.other` })}: ${value}`
      } else {
        newValue = formatMessage({ id: `customer.profile.${field.name}.${value}` })
      }
    }

    if (field.type === 'dialog' && field.dialog === 'customers') {
      newValue = customerMapping[newValue]?.name
    }

    if (field.roots) {
      labelText = formatMessage({ id: `customer.profile.${field.name}.roots` })
    } else if (field.label) {
      labelText = field.label
    } else {
      labelText = formatMessage({ id: `customer.profile.${field.name}` })
    }

    return <Grid item key={field.name} xs={12} sm={field.sm} md={field.md}>
      <TextField
        multiline={field.multiline}
        type="text"
        label={labelText}
        value={newValue}
        fullWidth
        variant="standard"
        size="small"
        margin="dense"
        InputProps={{
          readOnly: true,
        }}
      />
    </Grid>
  }

  function createCustomField(field) {
    let newValue = flatData[field.id] ? flatData[field.id] : ''

    if (field.type === 'dialog') {
      if (field.dialog === 'users') {
        newValue = userMapping[newValue] ? userMapping[newValue].displayName : ''
      } else {
        newValue = customerMapping[newValue] ? customerMapping[newValue].name : ''
      }
    } else if (field.type === 'check') {
      newValue = newValue ? '是' : '否'
    }

    return <Grid item key={field.id} xs={12} sm={field.sm} md={field.md}>
      <TextField
        type="text"
        label={field.name}
        value={newValue}
        fullWidth
        size="small"
        variant="standard"
        margin="dense"
        InputProps={{
          readOnly: true,
        }}
      />
    </Grid>
  }

  return (
    <div style={RootStyle} >
      <Grid container spacing={2} sx={{ padding: '0 20px 0 20px' }}>
        <EnhancedDivider readOnly={true} title={formatMessage({ id: 'customer.profile.basic' })}/>
        {printFields.basic.filter(i => i.active).map(i => ({
          ...i,
          sm: sizeMapping[i.size],
          md: sizeMapping[i.size],
        })).map(field => createField(field, customerData[field.name], customerData))}

      </Grid>
      {customerData['ICE'] && Object.keys(customerData['ICE']).length && printFields.ice.filter(i => i.active).length > 0 && <Grid container spacing={2} sx={{ padding: '0 20px 0 20px' }}>
        <EnhancedDivider readOnly={true} title={formatMessage({ id: 'customer.profile.emergencyContacts.root' })}/>
        {customerData['ICE'] && Object.keys(customerData['ICE'])
          .map(key => (printFields.ice
            .filter(i => i.active)
            .map(i => ({
              ...i,
              sm: sizeMapping[i.size],
              md: sizeMapping[i.size],
              type: 'emergencyContact'
            }))
            .map(field => createField(field, key, customerData)))
          )}
      </Grid>}

      {printFields.ext.filter(i => i.fields.filter(j => j.active).length > 0).map(i => (
        <Grid key={i.id} container spacing={2} sx={{ padding: '0 20px 0 20px' }}>
          <EnhancedDivider readOnly={true} title={i.name}/>
          {i.fields
            .filter(i => i.active && (!i.parent || (i.parent && flatData[i.pId])))
            .map(i => ({
              ...i,
              sm: sizeMapping[i.size],
              md: sizeMapping[i.size],
            }))
            .map(field => createCustomField(field))}
        </Grid>
      ))
      }

      {agreement &&

      <>
        <Spacer/>
        <Grid container spacing={2} sx={{ padding: '0 20px 40px 20px' }}>
          <Grid item xs={12} sm={12} md={12}>
            {agreement}
          </Grid>
          <Grid item xs={7} sm={7} md={7}></Grid>
          <Grid item xs={5} sm={5} md={5}>
            <TextField
              type="text"
              label={'客戶簽名'}
              value={' '}
              fullWidth
              size="small"
              variant="standard"
              margin="dense"
              InputProps={{
                readOnly: true,
              }}
            />
          </Grid>
        </Grid></>}
    </div>
  )
}

export default PrintCustomerData;